import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthStore } from './store/authStore';
import { checkAuthStatus } from './api/auth';

// Pages
import LoginPage from './pages/LoginPage';
import ChatPage from './pages/ChatPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
// NEU: Import der rechtlichen Seiten
import ImpressumPage from './pages/ImpressumPage';
import DatenschutzPage from './pages/DatenschutzPage';

// Protected route component
const ProtectedRoute = ({ children, admin = false }) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingContainer>Lade...</LoadingContainer>;
  }

  if (!isAuthenticated) {
    // Speichere die Ziel-URL, um nach dem Login dorthin weiterzuleiten (optional)
    // sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
    return <Navigate to="/login" />;
  }

  if (admin && user?.role !== 'admin') {
    // Optional: Weiterleitung zu einer "Kein Zugriff"-Seite statt zur Haupt-App
    return <Navigate to="/app" />;
  }

  return children;
};

function App() {
  const { setAuthStatus, setLoading, isAuthenticated } = useAuthStore(); // isAuthenticated hinzugefügt

  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      try {
        const userData = await checkAuthStatus();
        setAuthStatus(true, userData);
      } catch (error) {
        // Nur auf false setzen, wenn kein gültiger Status gefunden wurde
        setAuthStatus(false, null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [setAuthStatus, setLoading]); // Abhängigkeiten korrekt

  return (
    <Router>
      <Routes>
        {/* Öffentliche Routen */}
        <Route
          path="/login"
          element={
            // Wenn bereits authentifiziert, direkt zur App weiterleiten
            isAuthenticated ? <Navigate to="/app" replace /> : <LoginPage />
          }
        />
        {/* NEU: Routen für Impressum und Datenschutz */}
        <Route path="/impressum" element={<ImpressumPage />} />
        <Route path="/datenschutz" element={<DatenschutzPage />} />

        {/* Geschützte Routen */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        />

        {/* Route für spezifischen Chat */}
        <Route
          path="/app/chat/:chatId"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute admin>
              <AdminPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Redirect root to /app oder /login basierend auf Auth-Status */}
        <Route
          path="/"
          element={
            <Navigate to={isAuthenticated ? "/app" : "/login"} replace />
          }
        />

        {/* Catch all andere Routen - leitet zur App weiter, wenn eingeloggt, sonst zum Login */}
        <Route
          path="*"
          element={
            <Navigate to={isAuthenticated ? "/app" : "/login"} replace />
          }
        />
      </Routes>
    </Router>
  );
}

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.text}; /* Theme verwenden */
  background-color: ${({ theme }) =>
    theme.colors.backgroundAlt}; /* Theme verwenden */
`;

export default App;
